import { useState, useMemo, useEffect } from 'react'
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Box,
  Text,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Collapse,
  Link,
} from '@chakra-ui/react'
import { EmailRichComposer } from '@/components/email/EmailRichComposer'
import { useThemeColors } from '@/hooks/useThemeColors'
import { getRequestMessages, replyInThread } from '@/lib/services/caseThreadsService'
import { sendTicketRequestEmail } from '@/lib/services/ticketRequestService'
import { isEmptyHtml } from '@/lib/utils/emailAttachments'
import { useToastNotification } from '@/lib/utils/useToastNotification'

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1}>
        {label}
      </Text>
      <Text fontSize="sm">{value ?? '–'}</Text>
    </Box>
  )
}

function resolveRequestId(row: Record<string, unknown> | null): number | null {
  if (!row) return null
  const raw = row.requestDbId ?? row.request_db_id ?? row.id
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

type Props = {
  isOpen: boolean
  onClose: () => void
  row: Record<string, unknown> | null
  /**
   * compose — Incoming Requests (editable subject + request details block).
   * thread — Parent Overview / Case Threads style (locked subject, body-only reply API).
   */
  variant?: 'compose' | 'thread'
  onSent?: () => void
}

export function ComposeEmailDrawer({ isOpen, onClose, row, variant = 'compose', onSent }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [subject, setSubject] = useState('')
  const [cc, setCc] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [isSending, setIsSending] = useState(false)
  const [showRequestDetails, setShowRequestDetails] = useState(false)
  const [lockedSubject, setLockedSubject] = useState<string | null>(null)
  const [isLoadingSubject, setIsLoadingSubject] = useState(false)

  const isThread = variant === 'thread'
  const requestId = resolveRequestId(row)
  const requestNumber = (row?.request_number as string) ?? ''
  const toEmail = (row?.contact_email as string) ?? ''

  const defaultSubject = useMemo(
    () => (requestNumber ? `Re: Request ${requestNumber}` : ''),
    [requestNumber]
  )

  useEffect(() => {
    if (!row || !isOpen) return
    setCc('')
    setBody('')
    setAttachments([])
    setShowRequestDetails(false)
    if (!isThread) {
      setSubject(defaultSubject)
      setLockedSubject(null)
      return
    }

    setSubject('')
    setLockedSubject(null)
    if (!requestId) return

    let cancelled = false
    setIsLoadingSubject(true)
    void getRequestMessages(requestId)
      .then((data) => {
        if (cancelled) return
        const locked = (data.locked_subject || '').trim()
        setLockedSubject(locked || defaultSubject || null)
      })
      .catch(() => {
        if (!cancelled) setLockedSubject(defaultSubject || null)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSubject(false)
      })

    return () => {
      cancelled = true
    }
  }, [row?.id, row?.requestDbId, defaultSubject, isOpen, isThread, requestId])

  const displaySubject = isThread ? (lockedSubject ?? '') : subject || defaultSubject
  const effectiveSubject = isThread ? (lockedSubject || defaultSubject) : subject || defaultSubject

  const description = (row?.description as string) ?? '–'
  const bodyEmpty = isEmptyHtml(body)

  const handleSend = async () => {
    if (bodyEmpty || !requestId) return
    setIsSending(true)
    try {
      const cleanCc = cc.trim()
      if (isThread) {
        await replyInThread(requestId, body, cleanCc || undefined, false, attachments)
      } else {
        await sendTicketRequestEmail(
          requestId,
          {
            subject: effectiveSubject.trim() || undefined,
            body,
            cc: cleanCc || undefined,
            to: toEmail.trim() || undefined,
          },
          attachments
        )
      }
      showSuccess('Success', 'Email sent successfully')
      setSubject('')
      setCc('')
      setBody('')
      setAttachments([])
      onSent?.()
      onClose()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsSending(false)
    }
  }

  if (!row) return null

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent bg={colors.cardBg} display="flex" flexDirection="column">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={colors.borderLight} pb={3}>
          <Text fontSize="md" fontWeight="600" color={colors.text}>
            {isThread ? 'Email requestor' : 'Compose Email'}
          </Text>
          <Text fontSize="xs" color={colors.textSecondary} fontWeight="normal" mt={0.5} noOfLines={1}>
            To: {toEmail || '–'}
          </Text>
        </DrawerHeader>

        <DrawerBody py={4} flex="1" minH={0} overflowY="auto">
          <Box display="flex" flexDir="column" minH={0}>
            <Text fontSize="sm" color={colors.textSecondary} mb={3}>
              <Text as="span" fontWeight="700">
                From:
              </Text>{' '}
              alshelpdesk@actionlabs.com.ph
            </Text>

            <Divider borderColor={colors.borderLight} mb={3} />

            <FormControl mb={3}>
              <FormLabel fontSize="xs" color={colors.textSecondary} fontWeight="700" mb={1}>
                Cc
              </FormLabel>
              <Input
                size="sm"
                h="36px"
                fontSize="sm"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="email@example.com, another@example.com"
                bg={colors.inputBg}
                borderColor={colors.inputBorder}
                _hover={{ borderColor: colors.inputborderHover }}
                _focusVisible={{ borderColor: colors.brandBlue, boxShadow: 'none' }}
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel fontSize="xs" color={colors.textSecondary} fontWeight="700" mb={1}>
                Subject
              </FormLabel>
              <Input
                size="sm"
                h="36px"
                fontSize="sm"
                value={displaySubject}
                onChange={(e) => {
                  if (!isThread) setSubject(e.target.value)
                }}
                placeholder={isThread && isLoadingSubject ? 'Loading subject…' : 'Subject'}
                isReadOnly={isThread}
                isDisabled={isThread && isLoadingSubject}
                bg={colors.inputBg}
                borderColor={colors.inputBorder}
                _hover={{ borderColor: colors.inputborderHover }}
                _focusVisible={{ borderColor: colors.brandBlue, boxShadow: 'none' }}
              />
              {isThread ? (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Subject is locked to the Case Threads conversation.
                </Text>
              ) : null}
            </FormControl>

            {!isThread ? (
              <Box mb={3}>
                <Link
                  fontSize="xs"
                  color={colors.brandBlue}
                  fontWeight="600"
                  onClick={() => setShowRequestDetails((v) => !v)}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {showRequestDetails ? 'Hide request details' : 'Show request details'}
                </Link>
                <Collapse in={showRequestDetails} animateOpacity>
                  <Box pt={3}>
                    <DetailRow
                      label="Ticket request details"
                      value={<Text fontSize="sm" whiteSpace="pre-wrap">{description}</Text>}
                    />
                  </Box>
                </Collapse>
              </Box>
            ) : null}

            <FormControl display="flex" flexDir="column" flex="1" minH={0}>
              <FormLabel fontSize="xs" color={colors.textSecondary} fontWeight="700" mb={1}>
                Message
              </FormLabel>
              <EmailRichComposer
                value={body}
                onChange={setBody}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                placeholder="Type your message…"
                minHeight="200px"
                isDisabled={isSending}
              />
            </FormControl>
          </Box>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={colors.border} gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose} isDisabled={isSending}>
            Cancel
          </Button>
          <Button
            size="sm"
            bg={colors.brandBlue}
            color="white"
            _hover={{ bg: colors.brandBlueHover }}
            _active={{ bg: colors.brandBlueActive }}
            isLoading={isSending}
            loadingText="Sending"
            onClick={handleSend}
            isDisabled={isSending || bodyEmpty || !requestId || (isThread && isLoadingSubject)}
          >
            Send
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

/** @deprecated Prefer ComposeEmailDrawer */
export const ComposeEmailModal = ComposeEmailDrawer
